/**
 * Login Screen for the Dashboard
 *
 * Features:
 *   - Username + password login
 *   - Forgot password flow (6-digit email code)
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router";
import zapLogo from "figma:asset/f3ea20a5ac1d9a14ff41e77e5c2314b781a6a6e5.png";
import {
  User,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  KeyRound,
  ArrowRight,
  Mail,
  CheckCircle2,
  ArrowLeft,
} from "lucide-react";
import { useAuth } from "../shared/AuthContext";
import { requestPasswordReset, verifyResetCode } from "../../utils/userApi";


/* ================================================================
   Forgot Password Sub-Steps
   ================================================================ */

type ForgotStep = "username" | "code" | "newpass" | "done";

/* ---- shared input class strings (high-contrast) ---- */
const INPUT_CLS = "w-full pr-10 pl-4 py-3 rounded-xl text-sm outline-none bg-white/15 border border-white/25 text-white placeholder:text-blue-200/60 focus:border-blue-300 focus:bg-white/20";
const INPUT_ERR_CLS = "w-full pr-10 pl-4 py-3 rounded-xl text-sm outline-none bg-red-500/15 border border-red-400/60 text-white placeholder:text-red-200/60";
const ICON_CLS = "w-4 h-4 text-blue-200/70";

function ForgotPasswordFlow({ onBack, onComplete }: {
  onBack: () => void;
  onComplete: (username: string, code: string, newPassword: string) => void;
}) {
  const [step, setStep] = useState<ForgotStep>("username");
  const [username, setUsername] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPass, setShowNewPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 200);
  }, [step]);

  // Cooldown timer for resend
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleRequestCode = async () => {
    if (!username.trim()) return;
    setLoading(true);
    setError("");
    const result = await requestPasswordReset(username.trim());
    setLoading(false);
    if (result.error === "NETWORK_ERROR") {
      setError("\u05E9\u05D2\u05D9\u05D0\u05EA \u05EA\u05E7\u05E9\u05D5\u05E8\u05EA \u05DC\u05E9\u05E8\u05EA");
      return;
    }
    // Always advance (to prevent user enumeration)
    setCooldown(60);
    setStep("code");
  };

  const handleVerifyCode = async () => {
    if (code.length !== 6) return;
    setLoading(true);
    setError("");
    const result = await verifyResetCode(username.trim(), code.trim());
    setLoading(false);
    if (!result.valid) {
      setError(result.error || "\u05E7\u05D5\u05D3 \u05E9\u05D2\u05D5\u05D9");
      return;
    }
    setStep("newpass");
  };

  const handleSetPassword = () => {
    if (newPassword.length < 3) {
      setError("\u05E1\u05D9\u05E1\u05DE\u05D4 \u05D7\u05D3\u05E9\u05D4 \u05D7\u05D9\u05D9\u05D1\u05EA \u05DC\u05E4\u05D7\u05D5\u05EA 3 \u05EA\u05D5\u05D5\u05D9\u05DD");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("\u05D4\u05E1\u05D9\u05E1\u05DE\u05D0\u05D5\u05EA \u05DC\u05D0 \u05EA\u05D5\u05D0\u05DE\u05D5\u05EA");
      return;
    }
    onComplete(username.trim(), code.trim(), newPassword);
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setLoading(true);
    await requestPasswordReset(username.trim());
    setLoading(false);
    setCooldown(60);
    setError("");
  };

  return (
    <div className="space-y-4">
      {/* Step indicator */}
      <div className="flex items-center justify-center gap-2 mb-2">
        {(["username", "code", "newpass"] as ForgotStep[]).map((s, i) => (
          <div
            key={s}
            className={`h-2 rounded-full ${
              step === s ? "bg-blue-400 w-6" : i < (step === "code" ? 1 : step === "newpass" ? 2 : 0) ? "bg-blue-400/60 w-2" : "bg-white/25 w-2"
            }`}
          />
        ))}
      </div>

      {/* Step: Enter Username */}
      {step === "username" && (
        <>
          <div className="text-center mb-3">
            <KeyRound className="w-8 h-8 text-blue-300 mx-auto mb-2" />
            <h3 className="text-white text-sm" style={{ fontWeight: 600 }}>
              {"\u05E9\u05DB\u05D7\u05EA\u05D9 \u05E1\u05D9\u05E1\u05DE\u05D4"}
            </h3>
            <p className="text-blue-200 text-xs mt-1">
              {"\u05D4\u05D6\u05D9\u05E0\u05D5 \u05E9\u05DD \u05DE\u05E9\u05EA\u05DE\u05E9 \u05D5\u05E0\u05E9\u05DC\u05D7 \u05E7\u05D5\u05D3 \u05D0\u05D9\u05DE\u05D5\u05EA \u05DC\u05DE\u05D9\u05D9\u05DC"}
            </p>
          </div>
          <div className="relative">
            <User className={`absolute right-3 top-1/2 -translate-y-1/2 ${ICON_CLS}`} />
            <input
              ref={inputRef}
              type="text"
              value={username}
              onChange={(e) => { setUsername(e.target.value); setError(""); }}
              placeholder={"\u05E9\u05DD \u05DE\u05E9\u05EA\u05DE\u05E9"}
              className={error ? INPUT_ERR_CLS : INPUT_CLS}
              dir="ltr"
              onKeyDown={(e) => e.key === "Enter" && handleRequestCode()}
            />
          </div>
          {error && (
            <p className="text-xs text-red-300 flex items-center gap-1.5">
              <AlertCircle className="w-3 h-3" /> {error}
            </p>
          )}
          <button
            type="button"
            className="btnReset w-full py-3 bg-blue-500 text-white rounded-xl text-sm hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-blue-500/25"
            style={{ fontWeight: 600 }}
            disabled={!username.trim() || loading}
            onClick={handleRequestCode}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (
              <span className="flex items-center justify-center gap-2">
                <Mail className="w-4 h-4" />
                {"\u05E9\u05DC\u05D7 \u05E7\u05D5\u05D3 \u05D0\u05D9\u05DE\u05D5\u05EA"}
              </span>
            )}
          </button>
        </>
      )}

      {/* Step: Enter Code */}
      {step === "code" && (
        <>
          <div className="text-center mb-3">
            <Mail className="w-8 h-8 text-blue-300 mx-auto mb-2" />
            <h3 className="text-white text-sm" style={{ fontWeight: 600 }}>
              {"\u05D4\u05D6\u05D9\u05E0\u05D5 \u05D0\u05EA \u05D4\u05E7\u05D5\u05D3"}
            </h3>
            <p className="text-blue-200 text-xs mt-1">
              {"\u05E9\u05DC\u05D7\u05E0\u05D5 \u05E7\u05D5\u05D3 \u05D1\u05DF 6 \u05E1\u05E4\u05E8\u05D5\u05EA \u05DC\u05DE\u05D9\u05D9\u05DC \u05E9\u05DC\u05DA"}
            </p>
          </div>
          <div className="relative">
            <KeyRound className={`absolute right-3 top-1/2 -translate-y-1/2 ${ICON_CLS}`} />
            <input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => { setCode(e.target.value.replace(/\D/g, "")); setError(""); }}
              placeholder="000000"
              className={`${error ? INPUT_ERR_CLS : INPUT_CLS} text-center tracking-[0.5em] font-mono text-lg`}
              dir="ltr"
              onKeyDown={(e) => e.key === "Enter" && handleVerifyCode()}
              autoComplete="one-time-code"
            />
          </div>
          {error && (
            <p className="text-xs text-red-300 flex items-center gap-1.5">
              <AlertCircle className="w-3 h-3" /> {error}
            </p>
          )}
          <button
            type="button"
            className="btnReset w-full py-3 bg-blue-500 text-white rounded-xl text-sm hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-blue-500/25"
            style={{ fontWeight: 600 }}
            disabled={code.length !== 6 || loading}
            onClick={handleVerifyCode}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (
              <span className="flex items-center justify-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                {"\u05D0\u05DE\u05EA \u05E7\u05D5\u05D3"}
              </span>
            )}
          </button>
          <button
            type="button"
            className="btnReset w-full py-2 text-blue-200/80 text-xs hover:text-white disabled:opacity-30"
            disabled={cooldown > 0 || loading}
            onClick={handleResend}
          >
            {cooldown > 0
              ? `\u05E9\u05DC\u05D7 \u05E9\u05D5\u05D1 \u05D1\u05E2\u05D5\u05D3 ${cooldown} \u05E9\u05E0\u05D9\u05D5\u05EA`
              : "\u05E9\u05DC\u05D7 \u05E7\u05D5\u05D3 \u05E9\u05D5\u05D1"
            }
          </button>
        </>
      )}

      {/* Step: Set New Password */}
      {step === "newpass" && (
        <>
          <div className="text-center mb-3">
            <Lock className="w-8 h-8 text-blue-300 mx-auto mb-2" />
            <h3 className="text-white text-sm" style={{ fontWeight: 600 }}>
              {"\u05E1\u05D9\u05E1\u05DE\u05D4 \u05D7\u05D3\u05E9\u05D4"}
            </h3>
            <p className="text-blue-200 text-xs mt-1">
              {"\u05D1\u05D7\u05E8\u05D5 \u05E1\u05D9\u05E1\u05DE\u05D4 \u05D7\u05D3\u05E9\u05D4 \u05DC\u05D7\u05E9\u05D1\u05D5\u05DF"}
            </p>
          </div>
          <div className="relative">
            <Lock className={`absolute right-3 top-1/2 -translate-y-1/2 ${ICON_CLS}`} />
            <input
              ref={inputRef}
              type={showNewPass ? "text" : "password"}
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); setError(""); }}
              placeholder={"\u05E1\u05D9\u05E1\u05DE\u05D4 \u05D7\u05D3\u05E9\u05D4"}
              className={error ? INPUT_ERR_CLS : INPUT_CLS}
              dir="ltr"
              autoComplete="new-password"
            />
            <span
              role="button"
              tabIndex={0}
              onClick={() => setShowNewPass(!showNewPass)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setShowNewPass(!showNewPass); }}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-200/70 hover:text-white cursor-pointer"
            >
              {showNewPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </span>
          </div>
          <div className="relative">
            <Lock className={`absolute right-3 top-1/2 -translate-y-1/2 ${ICON_CLS}`} />
            <input
              type={showNewPass ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setError(""); }}
              placeholder={"\u05D0\u05D9\u05DE\u05D5\u05EA \u05E1\u05D9\u05E1\u05DE\u05D4"}
              className={error ? INPUT_ERR_CLS : INPUT_CLS}
              dir="ltr"
              autoComplete="new-password"
              onKeyDown={(e) => e.key === "Enter" && handleSetPassword()}
            />
          </div>
          {error && (
            <p className="text-xs text-red-300 flex items-center gap-1.5">
              <AlertCircle className="w-3 h-3" /> {error}
            </p>
          )}
          <button
            type="button"
            className="btnReset w-full py-3 bg-blue-500 text-white rounded-xl text-sm hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-blue-500/25"
            style={{ fontWeight: 600 }}
            disabled={!newPassword || !confirmPassword || loading}
            onClick={handleSetPassword}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (
              <span className="flex items-center justify-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                {"\u05E2\u05D3\u05DB\u05DF \u05E1\u05D9\u05E1\u05DE\u05D4 \u05D5\u05D4\u05EA\u05D7\u05D1\u05E8"}
              </span>
            )}
          </button>
        </>
      )}

      {/* Back button */}
      <button
        type="button"
        className="btnReset w-full py-2 text-blue-200/80 text-xs hover:text-white flex items-center justify-center gap-1"
        onClick={step === "username" ? onBack : () => setStep(step === "newpass" ? "code" : "username")}
      >
        <ArrowLeft className="w-3 h-3" />
        {step === "username" ? "\u05D7\u05D6\u05E8\u05D4 \u05DC\u05D4\u05EA\u05D7\u05D1\u05E8\u05D5\u05EA" : "\u05D7\u05D6\u05E8\u05D4"}
      </button>
    </div>
  );
}


/* ================================================================
   Main Login Screen
   ================================================================ */

export function LoginScreen() {
  const { login, loginWithResetCode, consumeReturnUrl } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const usernameRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!showForgotPassword) {
      usernameRef.current?.focus();
    }
  }, [showForgotPassword]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) return;
    setIsChecking(true);
    setError("");

    const result = await login(username.trim(), password.trim());
    if (!result.success) {
      setError(result.error || "\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05D4\u05EA\u05D7\u05D1\u05E8\u05D5\u05EA");
      setIsChecking(false);
      setPassword("");
    } else {
      const returnUrl = consumeReturnUrl();
      if (returnUrl && returnUrl !== "/") {
        navigate(returnUrl);
      }
    }
  };

  const handleForgotComplete = async (uname: string, code: string, newPass: string) => {
    setIsChecking(true);
    const result = await loginWithResetCode(uname, code, newPass);
    setIsChecking(false);
    if (result.success) {
      const returnUrl = consumeReturnUrl();
      if (returnUrl && returnUrl !== "/") {
        navigate(returnUrl);
      }
    } else {
      setError(result.error || "\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05D0\u05D9\u05E4\u05D5\u05E1 \u05D4\u05E1\u05D9\u05E1\u05DE\u05D4");
      setShowForgotPassword(false);
    }
  };

  return (
    <div
      dir="rtl"
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        fontFamily: "var(--font-family)",
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)",
      }}
    >
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[10%] right-[15%] w-72 h-72 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-[20%] left-[10%] w-96 h-96 bg-blue-400/8 rounded-full blur-3xl" />
        <div className="absolute top-[50%] left-[50%] w-64 h-64 bg-indigo-400/8 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      </div>

      <div className="relative w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src={zapLogo} alt="Zap Group" className="w-16 h-16 rounded-2xl mx-auto mb-4 shadow-lg shadow-black/30" />
          <h1 className="text-2xl text-white mb-1" style={{ fontWeight: 700 }}>
            {"ZAP WEB"}
          </h1>
          <p className="text-blue-200 text-sm">
            {"\u05D4\u05D6\u05D9\u05E0\u05D5 \u05E4\u05E8\u05D8\u05D9 \u05D4\u05EA\u05D7\u05D1\u05E8\u05D5\u05EA \u05DB\u05D3\u05D9 \u05DC\u05D4\u05D9\u05DB\u05E0\u05E1"}
          </p>
        </div>

        {/* Login card */}
        <div className="bg-slate-800/80 backdrop-blur-xl border border-slate-600/50 rounded-2xl p-6 shadow-2xl shadow-black/30">

          {showForgotPassword ? (
            <ForgotPasswordFlow
              onBack={() => setShowForgotPassword(false)}
              onComplete={handleForgotComplete}
            />
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Username */}
                <div>
                  <label className="text-sm text-slate-200 mb-2 block" style={{ fontWeight: 500 }}>
                    {"\u05E9\u05DD \u05DE\u05E9\u05EA\u05DE\u05E9"}
                  </label>
                  <div className="relative">
                    <User className={`absolute right-3 top-1/2 -translate-y-1/2 ${ICON_CLS}`} />
                    <input
                      ref={usernameRef}
                      type="text"
                      value={username}
                      onChange={(e) => { setUsername(e.target.value); setError(""); }}
                      placeholder="admin"
                      className={INPUT_CLS}
                      dir="ltr"
                      autoComplete="username"
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm text-slate-200" style={{ fontWeight: 500 }}>
                      {"\u05E1\u05D9\u05E1\u05DE\u05D4"}
                    </label>
                    <button
                      type="button"
                      className="btnReset text-xs text-blue-300 hover:text-white hover:underline"
                      onClick={() => { setShowForgotPassword(true); setError(""); }}
                    >
                      {"\u05E9\u05DB\u05D7\u05EA\u05D9 \u05E1\u05D9\u05E1\u05DE\u05D4?"}
                    </button>
                  </div>
                  <div className="relative">
                    <Lock className={`absolute right-3 top-1/2 -translate-y-1/2 ${ICON_CLS}`} />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setError(""); }}
                      placeholder={"\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"}
                      className={`${error ? INPUT_ERR_CLS : INPUT_CLS} !pl-10`}
                      dir="ltr"
                      autoComplete="current-password"
                    />
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => setShowPassword(!showPassword)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setShowPassword(!showPassword); }}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-200/70 hover:text-white cursor-pointer"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </span>
                  </div>
                  {error && (
                    <p className="text-xs text-red-300 mt-2 flex items-center gap-1.5">
                      <AlertCircle className="w-3 h-3" />
                      {error}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={!username.trim() || !password.trim() || isChecking}
                  className="w-full py-3 bg-blue-500 text-white rounded-xl text-sm hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-blue-500/25"
                  style={{ fontWeight: 600 }}
                >
                  {isChecking ? (
                    <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                  ) : (
                    "\u05DB\u05E0\u05D9\u05E1\u05D4"
                  )}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-[11px] text-slate-400 mt-6">
          {"ZAP WEB \u2014 \u05D6\u05D0\u05E4 \u05D2\u05E8\u05D5\u05E4"}
        </p>
      </div>
    </div>
  );
}